import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

interface ToastProps {
  message: string;
  duration?: number;
  onClose?: () => void;
}

export const Toast = ({ message, duration = 10000, onClose }: ToastProps) => {
  const theme = useTheme();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const remainingProgress = Math.max(
        0,
        100 - (elapsedTime / duration) * 100
      );

      if (remainingProgress === 0) {
        clearInterval(timer);
        onClose?.();
      } else {
        setProgress(remainingProgress);
      }
    }, 10);

    return () => clearInterval(timer);
  }, [duration, onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1%',
        right: '1%',
        zIndex: 9999,
        borderRadius: '4px',
        background: theme.palette.error.light,
        boxShadow: theme.shadows[10],
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        animation: 'errorFadeIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        minWidth: '300px',
        maxWidth: '500px',
      }}
    >
      <style>{`
@keyframes errorFadeIn {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
`}</style>

      <button
        style={{
          position: 'absolute',
          top: '5px',
          right: '0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'white',
        }}
        onClick={onClose}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          className='lucide lucide-x'
        >
          <path d='m6 6 12 12' />
          <path d='m18 6-12 12' />
        </svg>
      </button>

      <div
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          width: '100%',
          height: '3px',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '1px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: theme.palette.error.main,
            transition: 'width 10ms linear',
          }}
        />
      </div>

      <div style={{ padding: '16px 24px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: theme.palette.error.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
            }}
          >
            ⚠️
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }}
          >
            Oops!
          </h2>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.5',
          }}
        >
          {message}
        </p>
      </div>
    </div>
  );
};
